import { useEffect, useMemo, useState } from 'react';
import { AppWindow, Building2, Clock3, ShieldAlert, Users } from 'lucide-react';
import { useAnalytics } from '@/store/analytics';

import { PageHeader } from '@/components/ui/PageHeader';
import { DateRangePicker } from '@/components/analytics/date-range-picker';
import { MetricCard } from '@/components/analytics/metric-card';
import { TimeSeriesChart } from '@/components/analytics/time-series-chart';
import { UsageBreakdownTable } from '@/components/analytics/usage-breakdown-table';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';

export const AnalyticsPage = () => {
    const [days, setDays] = useState(30);
    const {
        summary,
        tenant,
        application,
        idp,
        isLoading,
        fetchSummary,
        fetchTenantAnalytics,
        fetchApplicationAnalytics,
        fetchIdpAnalytics,
    } = useAnalytics();
    const { setBreadcrumbs } = useLayout();

    useEffect(() => {
        setBreadcrumbs([{ name: 'Analytics' }]);
        fetchSummary();
        fetchTenantAnalytics(days);
        fetchApplicationAnalytics(days);
        fetchIdpAnalytics(days);
    }, [
        setBreadcrumbs,
        fetchSummary,
        fetchTenantAnalytics,
        fetchApplicationAnalytics,
        fetchIdpAnalytics,
        days,
    ]);

    const appRows = useMemo(() => {
        return (application?.applications || []).slice(0, 8).map((item) => ({
            label: item.name,
            value: item.enabled_tenants,
            secondary: `health=${item.health_status || 'unknown'}`,
        }));
    }, [application]);

    const topUsersRows = useMemo(() => {
        return (tenant?.top_active_users || []).map((item) => ({
            label: item.actor,
            value: item.login_count,
        }));
    }, [tenant]);

    const idpRows = useMemo(() => {
        return (idp?.tenant_breakdown || []).slice(0, 10).map((item) => ({
            label: `${item.display_name} / ${item.tenant_name}`,
            value: item.enabled_configs,
            secondary: `total=${item.total_configs}`,
        }));
    }, [idp]);

    if (isLoading && !summary) return <div className="h-full flex items-center justify-center"><Loader /></div>;


    return (
        <div className="p-4 lg:p-8 max-w-7xl mx-auto w-full animate-in fade-in duration-500">
            <PageHeader
                title="System Analytics"
                subtitle="Real-time aggregation of identity distribution and operational metrics."
                actions={<DateRangePicker value={days} onChange={setDays} />}
            />

            {summary?.status === 'disabled' && (
                <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 rounded-lg text-amber-800 dark:text-amber-300 flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 flex-shrink-0" />
                    <div>
                        <span className="font-semibold">Analytics Capture Disabled:</span> {summary?.reason || 'Connection String missing, analytics capture is disabled.'}
                    </div>
                </div>
            )}
            {summary?.status === 'api_key_missing' && (
                <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/50 rounded-lg text-amber-800 dark:text-amber-300 flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 flex-shrink-0" />
                    <div>
                        <span className="font-semibold">Query Restricted:</span> {summary?.reason || "API Key is missing, can't query analytics data."}
                    </div>
                </div>
            )}

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <MetricCard
                    title="Total Participants"
                    value={summary?.metrics.users || 0}
                    icon={Users}
                    description="The total number of registered users across all tenants in the system."
                />
                <MetricCard
                    title="Active Tenants"
                    value={summary?.metrics.tenants || 0}
                    icon={Building2}
                    description="The total number of active organizational tenants created in the system."
                />
                <MetricCard
                    title="Software Hubs"
                    value={summary?.metrics.applications || 0}
                    icon={AppWindow}
                    description="The total number of client applications registered and managed under the IAM platform."
                />
                <MetricCard
                    title="Security Vectors"
                    value={summary?.metrics.roles || 0}
                    icon={ShieldAlert}
                    description="The total number of security roles configured across the platform."
                />
                <MetricCard
                    title="Tenant DAU"
                    value={tenant?.dau || 0}
                    icon={Users}
                    hint={`MAU ${tenant?.mau || 0}`}
                    description="Daily Active Users (DAU) represents the average number of unique users authenticating to your tenants each day. Monthly Active Users (MAU) tracks unique authenticating users over a rolling 30-day window."
                />
                <MetricCard
                    title="App DAU"
                    value={application?.dau || 0}
                    icon={AppWindow}
                    hint={`MAU ${application?.mau || 0}`}
                    description="Daily Active Users per Application (DAU) measures the average number of unique users logging into registered client applications per day. Monthly Active Users (MAU) tracks unique application logins over a rolling 30-day window."
                />
                <MetricCard
                    title="IdP Avg Latency"
                    value={`${Math.round(idp?.avg_auth_latency_ms || 0)} ms`}
                    icon={Clock3}
                    description="The average response latency observed during authorization code exchanges with Identity Providers (e.g. Entra ID, Okta). Lower values indicate faster authentication handshakes."
                />
                <MetricCard
                    title="P95 Auth Latency"
                    value={`${Math.round(application?.p95_auth_latency_ms || 0)} ms`}
                    icon={Clock3}
                    description="95th Percentile Latency represents the response time threshold below which 95% of all authentication transactions fall. This measures real-world user experience and identifies performance outliers."
                />
            </div>

            <div className="mt-8 grid grid-cols-1 xl:grid-cols-2 gap-6">
                <div>
                    <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">Tenant Auth Time Series</h3>
                    <TimeSeriesChart data={(tenant?.auth_time_series || []).map((p) => ({ day: p.day, successes: p.successes, failures: p.failures }))} mode="auth" />
                </div>
                <div>
                    <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-3">Application Activity</h3>
                    <TimeSeriesChart data={(application?.activity || []).map((p) => ({ day: p.day, value: p.events }))} mode="single" />
                </div>
            </div>

            <div className="mt-6 grid grid-cols-1 xl:grid-cols-3 gap-6">
                <UsageBreakdownTable title="Top Active Users" rows={topUsersRows} />
                <UsageBreakdownTable title="Application Usage Breakdown" rows={appRows} />
                <UsageBreakdownTable title="IdP Tenant Breakdown" rows={idpRows} />
            </div>
        </div>
    );
};
